import { AppLayout, Container, ContentLayout, Header, SpaceBetween, Flashbar, Wizard, WizardProps } from '@amzn/awsui-components-core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContextProvider';
import { useWorkDocsAuth } from 'src/hooks/useWorkDocsAuth';
import { logger } from 'src/logger';
import { HomePageSideNavigationV2 } from 'src/components/side-navigation-v2/HomePageSideNavigationV2';
import { EmployeeListItem } from '../ea-huddle-v2/components/attendees/EmployeeListItem';
import { convertOrgChartEmployeeToEmployee } from './utils/employeeTypeAdapters';
import { OrgChartEmployee } from './types/orgChart.types';
import { buildRootFolderName } from './utils/workDocsFolderPlan';
import { useOrgChartData } from './hooks/useOrgChartData';
import { useWorkDocsFolderProvisioning } from './hooks/useWorkDocsFolderProvisioning';
import { useWorkDocsFolderBrowser } from './hooks/useWorkDocsFolderBrowser';
import { useWorkDocsUpload } from './hooks/useWorkDocsUpload';
import { useWorkDocsDelete } from './hooks/useWorkDocsDelete';
import { useFolderChecks } from './hooks/useFolderChecks';
import { useOrgCardGeneration } from './hooks/useOrgCardGeneration';
import { useBaseballCardWizard } from './hooks/useBaseballCardWizard';
import { useCardCategorySelection } from './hooks/useCardCategorySelection';
import { usePersistedWorkDocsFolderUrl } from './hooks/usePersistedWorkDocsFolderUrl';
import { mapViewModeToScope, computeExcludeAliases, selectedCategoryList } from './utils/orgCardRequest';
import { CARD_CATEGORY_OPTIONS, CARD_CATEGORY_VALUES } from './constants/BaseballCardConstants';
import { Step1ConnectWorkDocs } from './components/wizard/steps/Step1ConnectWorkDocs';
import { Step2FolderSetupAndSelect } from './components/wizard/steps/Step2FolderSetupAndSelect';
import { Step3ReviewAndUpload } from './components/wizard/steps/Step3ReviewAndUpload';
import { Step4Generate } from './components/wizard/steps/Step4Generate';
import { DisclaimerContainer } from './components/DisclaimerContainer';
import { PickUpWhereYouLeftOffContainer } from './components/PickUpWhereYouLeftOffContainer';
import './BaseballCardsHomePage.scss';

/** Returns true if an employee has a usable job level (mirrors the org-chart filter). */
const hasValidJobLevel = (employee: OrgChartEmployee): boolean =>
  employee.jobLevel !== null && employee.jobLevel !== undefined && employee.jobLevel.trim() !== '';

/** Zero-based index of the "Select People" step — the auto-advance target once folders provision. */
const SELECT_PEOPLE_STEP_INDEX = 1;

/** Zero-based index of the final "Generate Baseball Cards" step — where the submit gate applies. */
const GENERATE_STEP_INDEX = 3;

/**
 * Baseball Cards home page: a four-step Cloudscape Wizard that guides the user through the
 * required sequence — connect WorkDocs, provision/select, upload source docs, and generate
 * cards. This component is a thin orchestrator: all state and side-effects live in the
 * feature hooks; each step's UI lives in its own component.
 */
export const BaseballCardsHomePage: React.FC = () => {
  const { Alias, GivenName } = useAuth();
  const [navigationOpen, setNavigationOpen] = React.useState(false);

  // First name for the greeting hero. GivenName (Cognito custom:GIVEN_NAME) is a direct first
  // name, so no "Last, First" parsing is needed; fall back to the login alias if the claim is
  // absent (the cast is unchecked and can be undefined).
  const firstName = GivenName || Alias;

  const {
    isAuthenticated: isWorkDocsAuthenticated,
    initiateAuth: initiateWorkDocsAuth,
    isLoading: isWorkDocsAuthLoading,
    error: workDocsAuthError,
    accessToken: workDocsAccessToken
  } = useWorkDocsAuth();

  const org = useOrgChartData({ initialLeaderAlias: Alias });

  // Always provision the FULL org tree ('all'), regardless of the Step 2 Direct/All toggle. The
  // 'all' tree is a superset, so a later Direct/All change never needs a re-provision — the toggle
  // only affects which people are shown/selected, not the folder tree.
  const provisioning = useWorkDocsFolderProvisioning({ leaderAlias: org.currentLeaderAlias, viewMode: 'all' });

  const browser = useWorkDocsFolderBrowser({ rootFolderId: provisioning.rootFolderId });

  // Which folder's upload modal is open (null = closed). Lifted here (rather than local to
  // Step3ReviewAndUpload) so BOTH the folder tree's Upload button AND the Step 3 how-to guides can
  // open the same modal for a given folder.
  const [uploadFolderId, setUploadFolderId] = useState<string | null>(null);

  const categorySelection = useCardCategorySelection();

  // A stable fingerprint of everything that defines a generation run — leader, view mode, the
  // checked employees, and the chosen categories (plus the root folder). Arrays are sorted so the
  // signature is order-independent. useOrgCardGeneration clears its accepted jobId when this drifts
  // from what a run was submitted under, so editing any selection after generating re-offers
  // "Generate Cards" (hideSubmit keys off jobId) instead of leaving the button hidden.
  const selectionSignature = useMemo(
    () =>
      JSON.stringify({
        leader: org.currentLeaderAlias,
        view: org.viewMode,
        root: provisioning.rootFolderId,
        employees: Array.from(org.selectedEmployees).sort(),
        categories: Array.from(categorySelection.selectedCategories).sort()
      }),
    [org.currentLeaderAlias, org.viewMode, provisioning.rootFolderId, org.selectedEmployees, categorySelection.selectedCategories]
  );

  const orgGen = useOrgCardGeneration({ selectionSignature });

  // A successful upload refreshes the folder listing AND clears any completed generation state:
  // adding new source material after generating makes the run stale, so re-show the Next/"Generate
  // Cards" buttons (hideSubmit keys off orgGen.jobId). This covers the one staleness trigger the
  // selection signature can't see — a document upload doesn't change any selection. Every other
  // change (employees/categories/leader/view) is auto-cleared inside useOrgCardGeneration.
  const upload = useWorkDocsUpload({
    onUploaded: async (folderId) => {
      await browser.refreshFolder(folderId);
      orgGen.reset();
    }
  });

  // Deleting a source document reuses the exact same post-mutation path as upload: re-list the
  // folder so the row disappears, and clear completed generation state (removing source material
  // after generating likewise makes the run stale).
  const del = useWorkDocsDelete({
    onDeleted: async (folderId) => {
      await browser.refreshFolder(folderId);
      orgGen.reset();
    }
  });

  // Concrete employee objects that will get a card: valid job level, still selected, and
  // NOT the leader. The leader never generates a card for themselves — the org-card run fans
  // out per report and the backend skips the leader — so they must be excluded from the
  // Step 4 (Generate) count and request (no leader doc is expected).
  const selectedEmployeeObjects = useMemo(() => {
    const result: OrgChartEmployee[] = [];
    org.employees.forEach((employee, login) => {
      if (login !== org.currentLeaderAlias && hasValidJobLevel(employee) && org.selectedEmployees.has(login)) {
        result.push(employee);
      }
    });
    return result;
  }, [org.employees, org.selectedEmployees, org.currentLeaderAlias]);

  // Token-free fallback for the "Open My WorkDocs Folder" link: the persisted folder id saved on
  // the signed-in user's own baseball card. Lets a returning user with an expired WorkDocs token
  // still reach their folder (the live, token-verified `provisioning.rootFolderUrl` takes priority
  // when a token is present).
  const persistedFolder = usePersistedWorkDocsFolderUrl(Alias);
  const effectiveWorkDocsFolderUrl = provisioning.rootFolderUrl ?? persistedFolder.url;

  useEffect(() => {
    logger.info('User authenticated successfully!', { app: 'Baseball Cards' });
  }, []);

  // Auto-provision the WorkDocs folder tree as soon as the user connects (Step 1) — no manual
  // "Set Up" button. Fires only when: authenticated, a provision run is allowed (token + parent
  // known + not busy), no quarter root is known yet, and neither the verify nor a provision run is
  // in flight. The hook's verify-by-name effect runs first and adopts an already-existing quarter
  // root (so returning users don't re-provision); this only kicks in when nothing was found.
  // Provisioning is idempotent (find-or-create) regardless, so a redundant run is harmless.
  useEffect(() => {
    if (
      isWorkDocsAuthenticated &&
      provisioning.canProvision &&
      !provisioning.rootFolderId &&
      !provisioning.isProvisioning &&
      !provisioning.isVerifying
    ) {
      provisioning.provision();
    }
  }, [
    isWorkDocsAuthenticated,
    provisioning.canProvision,
    provisioning.rootFolderId,
    provisioning.isProvisioning,
    provisioning.isVerifying,
    provisioning.provision
  ]);

  // PDF generation requires WorkDocs fully set up: a valid token AND a provisioned root
  // folder with no outstanding provisioning error.
  const isWorkDocsReadyForPdf = isWorkDocsAuthenticated && !!provisioning.rootFolderId && !provisioning.error;

  const handleGenerate = useCallback(() => {
    // Guard: the submit button can fire even when prerequisites are unmet; no-op then so
    // Step 5's warnings tell the user what's missing rather than starting an empty run.
    // We also need the WorkDocs token, the provisioned root folder id, and the submitter's
    // alias to build the request — all three are prerequisites of a real run.
    if (!isWorkDocsReadyForPdf || selectedEmployeeObjects.length === 0) return;
    if (!workDocsAccessToken || !provisioning.rootFolderId || !Alias) return;
    // At least one card category must be selected — an empty run reads no source documents.
    if (categorySelection.selectedCategories.size === 0) return;

    // exclude_aliases = candidate logins that are NOT selected (unchecked employees).
    const excludeAliases = computeExcludeAliases(Array.from(org.employees.keys()), org.selectedEmployees);

    // Send exactly the checked categories (canonical order, filtered to valid API values).
    const categories = selectedCategoryList(categorySelection.selectedCategories, CARD_CATEGORY_VALUES);

    void orgGen.submit({
      leaderAlias: org.currentLeaderAlias,
      submitterAlias: Alias,
      rootFolderId: provisioning.rootFolderId,
      accessToken: workDocsAccessToken,
      scope: mapViewModeToScope(org.viewMode),
      excludeAliases,
      categories
    });
  }, [
    orgGen,
    selectedEmployeeObjects.length,
    isWorkDocsReadyForPdf,
    workDocsAccessToken,
    provisioning.rootFolderId,
    Alias,
    org.employees,
    org.selectedEmployees,
    org.currentLeaderAlias,
    org.viewMode,
    categorySelection.selectedCategories
  ]);

  // Forward-navigation gates per step index. Step 1 now owns BOTH connecting AND provisioning
  // (folders auto-provision on token), so leaving Step 1 requires a token and a provisioned root.
  // Steps 2/3 are free (provisioning is already done upstream).
  const stepGates = useMemo(
    () => [
      isWorkDocsAuthenticated && !!provisioning.rootFolderId && !provisioning.error, // 0 -> 1: token + folders
      true, // 1 -> 2: free
      true // 2 -> 3: free
    ],
    [isWorkDocsAuthenticated, provisioning.rootFolderId, provisioning.error]
  );

  const blockedMessages = useMemo(() => ['Connect WorkDocs and wait for folder setup to finish.', '', ''], []);

  // stepCount = index of the last (Generate) step + 1. Passed so the wizard hook can seed
  // Cloudscape's nav high-water mark and make every step clickable from the start.
  const wizard = useBaseballCardWizard({ stepGates, blockedMessages, stepCount: GENERATE_STEP_INDEX + 1, onSubmit: handleGenerate });

  // Advisory folder checks surfaced next to the Step 4 (Generate) category checkboxes: is a
  // Manager Hub ratings export present, and which selected people are missing supplemental docs.
  // Auto-runs when Generate is the active step (so a Step 3 upload is reflected on arrival) and
  // re-arms when the selection or root folder changes. Never blocks generation.
  const folderChecks = useFolderChecks({
    rootFolderId: provisioning.rootFolderId,
    selectedEmployees: selectedEmployeeObjects,
    isActive: wizard.activeStepIndex === GENERATE_STEP_INDEX
  });

  // Cloudscape's Wizard has no prop to disable the Next/submit button, so we grey it out via
  // scoped CSS toggled by these wrapper modifiers (see BaseballCardsHomePage.scss).
  // - nextDisabled: the current step's forward gate is unmet (Step 1 token/folders), or — on the
  //   final Generate step — no card category is selected OR no employees were selected in Step 2
  //   (either makes for an empty run, so "Generate Cards" is disabled).
  // - hideSubmit: a generation run has been started (or is starting) — hide "Generate Cards".
  //   Scoped to the final step: on steps 1-4 the same footer primary button is "Next", so hiding
  //   it there would strand the user. jobId is cleared — re-showing "Generate Cards" — whenever the
  //   run goes stale: any selection change (auto-cleared via selectionSignature in useOrgCardGeneration)
  //   or a new document upload (the onUploaded reset above).
  const noCategoriesSelected = categorySelection.selectedCategories.size === 0;
  const noEmployeesSelected = selectedEmployeeObjects.length === 0;
  const nextDisabled =
    stepGates[wizard.activeStepIndex] === false || (wizard.activeStepIndex === GENERATE_STEP_INDEX && (noCategoriesSelected || noEmployeesSelected));
  const hideSubmit = wizard.activeStepIndex === GENERATE_STEP_INDEX && (orgGen.isSubmitting || !!orgGen.jobId);

  // Auto-advance from Step 1 to Step 2 (Select People) as soon as folders provision cleanly. The
  // counter is bumped ONLY on an active provision run that both created folders AND persisted the
  // reference — never by the passive load-time verify — so a returning user whose quarter root
  // already exists is NOT yanked forward; they land on Step 1 and click Next. The `> 0` guard
  // skips the initial (counter === 0) render. Depend on `wizard.goToStep` (a stable useCallback),
  // NOT the whole `wizard` object (a fresh literal each render), so the effect fires once per clean
  // provision rather than on unrelated re-renders.
  useEffect(() => {
    if (provisioning.provisionSuccessCount > 0) {
      wizard.goToStep(SELECT_PEOPLE_STEP_INDEX);
    }
  }, [provisioning.provisionSuccessCount, wizard.goToStep]);

  const steps: WizardProps.Step[] = useMemo(
    () => [
      {
        title: 'Connect to WorkDocs',
        description:
          "Link your WorkDocs account to get started. The tool uses your cloud drive to create folders and save your cards. Nothing is stored on your local machine. You'll sign in once and return here automatically.",
        content: (
          <Step1ConnectWorkDocs
            isAuthenticated={isWorkDocsAuthenticated}
            isLoading={isWorkDocsAuthLoading}
            error={workDocsAuthError}
            onGetToken={initiateWorkDocsAuth}
            isProvisioning={provisioning.isProvisioning}
            provisioningProgress={provisioning.progress}
            provisioningError={provisioning.error}
            provisioningTokenExpired={provisioning.tokenExpired}
            provisioningWarning={provisioning.warning}
            isProvisioned={!!provisioning.rootFolderId}
            onReconnect={initiateWorkDocsAuth}
          />
        ),
        errorText: wizard.activeStepIndex === 0 ? wizard.stepError || undefined : undefined
      },
      {
        title: 'Select People',
        description: 'Choose your scope, direct reports or your full org, then select who gets a card.',
        content: (
          <Step2FolderSetupAndSelect
            viewMode={org.viewMode}
            onViewModeChange={org.handleViewModeChange}
            currentLeaderData={org.currentLeaderData}
            onLeaderChange={org.handleLeaderChange}
            employees={org.filteredEmployeesForDisplay}
            expandedNodes={org.expandedNodes}
            selectedEmployees={org.selectedEmployees}
            onExpand={org.handleExpand}
            onCollapse={org.handleCollapse}
            onToggleSelection={org.handleToggleSelection}
            isLoadingOrg={org.isLoading}
            loadingProgress={org.loadingProgress}
            totalEmployees={org.totalEmployees}
          />
        ),
        errorText: wizard.activeStepIndex === 1 ? wizard.stepError || undefined : undefined
      },
      {
        title: 'Review & Upload Documents',
        description:
          'Expand any folder below to see its contents, then use the upload button to add files directly to WorkDocs. Files save instantly on upload. All folders are optional. If nothing is provided, the card is still generated without ratings or performance/potential information, based on the default internal source criteria.',
        content: (
          <Step3ReviewAndUpload
            rootFolderId={provisioning.rootFolderId}
            rootFolderName={buildRootFolderName(org.currentLeaderAlias)}
            expandedIds={browser.expandedIds}
            nodeStateById={browser.nodeStateById}
            onExpandFolder={browser.expandFolder}
            onCollapseFolder={browser.collapseFolder}
            isUploading={upload.isUploading}
            statusByFolder={upload.statusByFolder}
            onUpload={upload.uploadFiles}
            onDeleteFile={del.deleteFile}
            uploadFolderId={uploadFolderId}
            onSetUploadFolderId={setUploadFolderId}
            selectedAliases={org.selectedEmployees}
            alias={Alias}
          />
        )
      },
      {
        // Count of people who will get a card is surfaced right in the step title, e.g.
        // "Generate Baseball Cards (12)".
        title: `Generate Baseball Cards (${selectedEmployeeObjects.length})`,
        description: 'Kick off card creation for your selected people.',
        content: (
          <Step4Generate
            selectedEmployeeCount={selectedEmployeeObjects.length}
            isSubmitting={orgGen.isSubmitting}
            jobId={orgGen.jobId}
            error={orgGen.error}
            isWorkDocsReady={isWorkDocsReadyForPdf}
            categoryOptions={CARD_CATEGORY_OPTIONS}
            selectedCategories={categorySelection.selectedCategories}
            onToggleCategory={categorySelection.toggleCategory}
            isCheckingFolders={folderChecks.isRunning}
            managerHubCheck={folderChecks.checks?.managerHub ?? null}
            supplementalCheck={folderChecks.checks?.supplemental ?? null}
            employees={org.employees}
          />
        )
      }
    ],
    [
      isWorkDocsAuthenticated,
      isWorkDocsAuthLoading,
      workDocsAuthError,
      initiateWorkDocsAuth,
      org,
      provisioning,
      browser,
      upload,
      del,
      orgGen,
      selectedEmployeeObjects,
      isWorkDocsReadyForPdf,
      wizard.activeStepIndex,
      wizard.stepError,
      categorySelection.selectedCategories,
      categorySelection.toggleCategory,
      folderChecks.checks,
      folderChecks.isRunning
    ]
  );

  // Page-level flashes that persist across steps: org-load failures only. The generation-started
  // success confirmation is shown by Step 4's own in-step Alert (see Step4Generate), so we do NOT
  // duplicate it here in the top Flashbar.
  const flashItems = org.errorMessage
    ? [{ type: 'error' as const, content: org.errorMessage, dismissible: true, onDismiss: () => org.setErrorMessage(null), id: 'org-error' }]
    : [];

  return (
    <AppLayout
      navigation={<HomePageSideNavigationV2 />}
      navigationOpen={navigationOpen}
      onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
      contentType="default"
      toolsHide
      notifications={<Flashbar items={flashItems} />}
      content={
        <ContentLayout
          header={
            <div className="baseball-cards-page-header">
              <Header
                variant="h1"
                description={
                  <i className="baseball-cards-page-header__subtitle">
                    {'Generate AI-drafted "baseball card" summaries to prepare for talent reviews.'}
                  </i>
                }
              >
                Talent Review Card Builder
              </Header>
              {org.currentLeaderData && (
                <EmployeeListItem
                  employee={convertOrgChartEmployeeToEmployee(org.currentLeaderData)}
                  showDetails
                  showLevel={false}
                  levelInlineBeforeCity
                  disableHover
                />
              )}
            </div>
          }
        >
          <SpaceBetween size="l">
            <PickUpWhereYouLeftOffContainer workDocsFolderUrl={effectiveWorkDocsFolderUrl} alias={Alias} firstName={firstName} />

            {/* The whole wizard lives in one titled Container. The title uses <Header variant="h2">
                to match the "Pick Up Where You Left Off" container's header font size. The wrapper
                class still scopes the footer-button overrides (see BaseballCardsHomePage.scss) to
                this Wizard only — Cloudscape's footer has no first-class hide-Cancel, disable-Next,
                or hide-Submit props; modifiers toggle the Next-disabled and Submit-hidden states. */}
            <Container header={<Header variant="h2">Build or Refresh Your Cards</Header>}>
              <div
                className={`baseball-cards-wizard${nextDisabled ? ' baseball-cards-wizard--next-disabled' : ''}${
                  hideSubmit ? ' baseball-cards-wizard--submit-hidden' : ''
                }`}
              >
                <Wizard
                  steps={steps}
                  activeStepIndex={wizard.activeStepIndex}
                  onNavigate={({ detail }) => wizard.onNavigate(detail)}
                  onSubmit={handleGenerate}
                  submitButtonText="Generate Cards"
                  isLoadingNextStep={orgGen.isSubmitting}
                  i18nStrings={{
                    stepNumberLabel: (stepNumber) => `Step ${stepNumber}`,
                    collapsedStepsLabel: (stepNumber, stepsCount) => `Step ${stepNumber} of ${stepsCount}`,
                    navigationAriaLabel: 'Steps',
                    cancelButton: 'Cancel',
                    previousButton: 'Previous',
                    nextButton: 'Next',
                    optional: 'optional'
                  }}
                />
              </div>
            </Container>

            <DisclaimerContainer />
          </SpaceBetween>
        </ContentLayout>
      }
    />
  );
};
